import { useEffect, useState } from "react";
import { usePersistedState, useScrollRestore } from "@/hooks/useScrollRestore";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OptimizedImage } from "@/components/OptimizedImage";
import { partsApi, formatPrice, getFileUrl, PART_CATEGORIES, PART_CONDITIONS } from "@/lib/api";
import { toast } from "sonner";
import { 
  Plus, 
  Search,
  Package,
  MapPin,
  Filter,
  Grid,
  List
} from "lucide-react";

export default function PartsListPage() {
  const [parts, setParts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = usePersistedState("parts:search", "");
  const [categoryFilter, setCategoryFilter] = usePersistedState("parts:cat", "all");
  const [viewMode, setViewMode] = useState("grid");
  useScrollRestore("parts", !loading);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [partsData, statsData] = await Promise.all([
        partsApi.getAll(),
        partsApi.getStats()
      ]);
      setParts(partsData);
      setStats(statsData);
    } catch (error) {
      toast.error("Erreur lors du chargement des pièces");
    } finally {
      setLoading(false);
    }
  };

  const filteredParts = parts.filter(part => {
    const matchesSearch = !searchTerm || 
      part.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.part_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.part_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.compatible_models.some(m => m.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = categoryFilter === "all" || part.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-primary font-mono">Incarcare...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="parts-list">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading font-black text-3xl md:text-4xl">
              Piese de Schimb
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestioneaza stocul de piese second-hand
            </p>
          </div>
          <Link to="/parts/add">
            <Button 
              className="bg-primary text-black font-bold uppercase tracking-wide hover:bg-primary/90"
              data-testid="btn-add-part"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adauga Piesa
            </Button>
          </Link>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-secondary/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-mono font-bold text-2xl">{stats.total}</p>
                    <p className="text-xs text-muted-foreground">Total piese</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-500/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="font-mono font-bold text-2xl text-green-500">{stats.in_stock}</p>
                    <p className="text-xs text-muted-foreground">In stoc</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-red-500/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-red-500" />
                  <div>
                    <p className="font-mono font-bold text-2xl text-red-500">{stats.out_of_stock}</p>
                    <p className="text-xs text-muted-foreground">Epuizat</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-primary/10">
              <CardContent className="p-4">
                <div>
                  <p className="font-mono font-bold text-xl text-primary">{formatPrice(stats.total_value)}</p>
                  <p className="text-xs text-muted-foreground">Valoare stoc</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Cauta dupa nume, numar, model..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-parts"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-category">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Categorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate categoriile</SelectItem>
              {Object.entries(PART_CATEGORIES).map(([key, { label, icon }]) => (
                <SelectItem key={key} value={key}>
                  {icon} {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button
              variant={viewMode === "grid" ? "default" : "outline"}
              size="icon"
              onClick={() => setViewMode("grid")}
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "outline"}
              size="icon"
              onClick={() => setViewMode("list")}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Parts Grid/List */}
        {filteredParts.length > 0 ? (
          <div className={viewMode === "grid" 
            ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            : "space-y-3"
          }>
            {filteredParts.map((part) => (
              <PartCard key={part.id} part={part} viewMode={viewMode} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchTerm || categoryFilter !== "all" 
                ? "Nicio piesa gasita cu aceste criterii"
                : "Nicio piesa in stoc"}
            </p>
            <Link to="/parts/add">
              <Button className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Adauga o piesa
              </Button>
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}

function PartCard({ part, viewMode }) {
  const categoryInfo = PART_CATEGORIES[part.category] || PART_CATEGORIES.autres;
  const conditionInfo = PART_CONDITIONS[part.condition] || PART_CONDITIONS.bon;
  const defaultImage = "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop";

  if (viewMode === "list") {
    return (
      <Link to={`/parts/${part.part_id}`}>
        <Card className="hover:border-primary/50 transition-colors" data-testid={`part-card-${part.part_id}`}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
              <OptimizedImage
                src={part.photos?.[0] ? getFileUrl(part.photos[0]) : defaultImage}
                alt={part.name}
                fallback={defaultImage}
                aspectRatio="1/1"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">{categoryInfo.icon}</span>
                <span className="font-mono text-xs text-primary">{part.part_id}</span>
                <span className={`px-2 py-0.5 rounded text-xs text-white ${conditionInfo.color}`}>
                  {conditionInfo.label}
                </span>
              </div>
              <h3 className="font-heading font-bold truncate">{part.name}</h3>
              <p className="text-sm text-muted-foreground truncate">
                {part.compatible_brands.join(", ")} • {part.compatible_models.join(", ")}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="font-mono font-bold text-lg text-primary">{formatPrice(part.price)}</p>
              <p className="text-sm text-muted-foreground">Qté: {part.quantity}</p>
              {part.storage_location && (
                <p className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                  <MapPin className="w-3 h-3" />
                  {part.storage_location}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  }

  return (
    <Link to={`/parts/${part.part_id}`}>
      <Card className="overflow-hidden hover:border-primary/50 transition-colors group" data-testid={`part-card-${part.part_id}`}>
        <div className="relative overflow-hidden">
          <OptimizedImage
            src={part.photos?.[0] ? getFileUrl(part.photos[0]) : defaultImage}
            alt={part.name}
            fallback={defaultImage}
            className="group-hover:scale-105 transition-transform duration-300"
            aspectRatio="4/3"
          />
          <div className="absolute top-2 left-2 z-10 flex gap-1">
            <span className="bg-black/70 text-white px-2 py-1 rounded text-xs font-mono">
              {part.part_id}
            </span>
          </div>
          <div className="absolute top-2 right-2 z-10">
            <span className={`px-2 py-1 rounded text-xs text-white ${conditionInfo.color}`}>
              {conditionInfo.label}
            </span>
          </div>
          {part.quantity === 0 && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-10">
              <span className="text-white font-bold">ÉPUISÉ</span>
            </div>
          )}
        </div>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">{categoryInfo.icon}</span>
            <span className="text-xs text-muted-foreground">{categoryInfo.label}</span>
          </div>
          <h3 className="font-heading font-bold truncate">{part.name}</h3>
          <p className="text-sm text-muted-foreground truncate mb-2">
            {part.compatible_brands.slice(0, 2).join(", ")}
            {part.compatible_brands.length > 2 && "..."}
          </p>
          <div className="flex items-center justify-between">
            <p className="font-mono font-bold text-primary">{formatPrice(part.price)}</p>
            <p className="text-sm text-muted-foreground">Qté: {part.quantity}</p>
          </div>
          {part.storage_location && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-2">
              <MapPin className="w-3 h-3" />
              {part.storage_location}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}
